// client/src/pages/seo/QrCodeMenuForRestaurantsNo.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const sections = [
  { id: "answer", label: "Hva er en QR-kode-meny?" },
  { id: "create", label: "Lag en QR-kode til menyen: slik fungerer det" },
  { id: "pdf", label: "PDF eller en ekte digital meny?" },
  { id: "provider", label: "Hva bør restauranter se etter hos en leverandør?" },
  { id: "bestplace", label: "Hvordan fungerer en QR-meny med Best Place?" },
  { id: "placement", label: "Hvor bør QR-koden plasseres i en restaurant?" },
  { id: "faq", label: "Vanlige spørsmål om QR-kode-menyer" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuForRestaurantsNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title = "QR-kode-meny for restauranter | Lag digital meny | Best Place";
    setMeta(
      "description",
      "QR-kode-meny for restauranter: lag en digital meny, del den med QR-kode og oppdater priser, retter og språk på nett når som helst."
    );
    setCanonical("https://best-place.club/no/qr-kode-meny-for-restauranter");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          en: "/en/qr-code-menu-for-restaurants",
          de: "/de/qr-code-speisekarte",
          no: "/no/qr-kode-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR-kode-meny for restauranter</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Gjestene skanner QR-koden ved bordet og åpner den aktuelle menyen direkte på mobilen. Menyen åpnes i nettleseren, uten app og uten innlogging.
              </p>

              <p className="seoHero__text">
                QR-koden forblir den samme mens du oppdaterer priser, retter, bilder, tilgjengelighet og språk på nett. Det betyr at bordskilt eller kort ikke må trykkes på nytt hver gang noe endres.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="acquaintance">
        <div className="seoComparison">
          <div className="seoComparison__col seoComparison__col--highlight">
            <h2>Dette får du med en QR-kode-meny</h2>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Åpne menyen direkte på mobilen</li>
              <li className="text__seo-li">ingen app for gjestene</li>
              <li className="text__seo-li">oppdater priser og retter med en gang</li>
              <li className="text__seo-li">bilder, kategorier og beskrivelser</li>
              <li className="text__seo-li">flere språk tilgjengelig</li>
              <li className="text__seo-li">samme QR-kode etter oppdateringer</li>
            </ul>
          </div>

          <div className="seoComparison__col">
            <h2>Hva QR-koden ikke er</h2>
            <p>
              Selve QR-koden er bare inngangspunktet. Det som betyr noe, er siden som åpnes etterpå. En vanskelig PDF bak en QR-kode er teknisk sett digital, men er ofte langt mindre praktisk for gjestene enn en mobiltilpasset menyside.
            </p>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold</h2>
        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="answer" className="acquaintance">
          <h2>Hva er en QR-kode-meny?</h2>
          <p>
            En QR-kode-meny kobler en trykt QR-kode til en restaurantmeny på nett. Gjesten skanner koden med mobilkameraet, trykker på lenken og ser menyen i nettleseren.
          </p>
          <p>
            For restauranten er den virkelige fordelen ikke QR-koden i seg selv, men målsiden som kan redigeres. Hvis en pris endres, en rett blir utsolgt eller et sesongtilbud legges til, oppdaterer du innholdet på nett. Den allerede trykte QR-koden kan være helt den samme.
          </p>
          <p>
            Derfor bør en QR-meny ikke behandles som en engangs digital erstatning for papir, men som et løpende vedlikeholdt digitalt inngangspunkt til menyen.
          </p>
        </section>

        <section id="create" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Lag en QR-kode til menyen: slik fungerer det</h2>
            <p>
              Hvis du vil lage en QR-kode til menyen, bør du ikke starte med selve QR-bildet. Start med målsiden. Koden er bare så god som menyen som åpnes etter skanningen.
            </p>

            <div className="qrArticle__cardGrid">
              <div>
                <strong>1. Lag den digitale menyen</strong>
                <p>Legg til kategorier, retter, priser, beskrivelser og bilder.</p>
              </div>
              <div>
                <strong>2. Sjekk gjestevisningen</strong>
                <p>Åpne menyen på en ekte mobil og test den.</p>
              </div>
              <div>
                <strong>3. Generer QR-koden</strong>
                <p>Koble koden til en stabil meny-URL.</p>
              </div>
              <div>
                <strong>4. Skann før trykking</strong>
                <p>Test med flere enheter, lysforhold og avstander.</p>
              </div>
            </div>

            <p>
              Med Best Place kan du først bygge den digitale menyen og deretter dele den samme menylenken via QR-kode, nettside eller melding.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>QR-kode med PDF eller en ekte digital meny?</h2>
          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>QR-kode med PDF</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">rask å sette opp</li>
                <li className="text__seo-li">krever ofte zooming på små skjermer</li>
                <li className="text__seo-li">endringer krever vanligvis en ny fil</li>
                <li className="text__seo-li">enkeltprodukter er vanskeligere å vedlikeholde</li>
              </ul>
            </div>
            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Mobil digital meny</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">laget for smarttelefoner</li>
                <li className="text__seo-li">tydelige kategorier og lesbare priser</li>
                <li className="text__seo-li">produkter kan oppdateres enkeltvis</li>
                <li className="text__seo-li">samme QR-kode selv når innholdet endres</li>
              </ul>
            </div>
          </div>
          <p>
            Hvis menyen nesten aldri endres, kan en PDF være nok. Hvis du jevnlig administrerer priser, produkter, tilgjengelighet, bilder eller språk, er en ekte digital menyside vanligvis mer fleksibel.
          </p>
        </section>

        <section id="provider" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør restauranter se etter hos en leverandør av QR-menyer?</h2>
            <p>
              De fleste løsninger kan generere en QR-kode. I den daglige restaurantdriften er de andre kriteriene viktigere enn selve koden.
            </p>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li"><strong>Mobilbruk:</strong> meny uten zooming og uten app.</li>
              <li className="text__seo-li"><strong>Redigering:</strong> endre priser og retter uten utvikler.</li>
              <li className="text__seo-li"><strong>Stabil URL:</strong> den trykte QR-koden fortsetter å fungere.</li>
              <li className="text__seo-li"><strong>Språk:</strong> internasjonale gjester kan velge sitt språk.</li>
              <li className="text__seo-li"><strong>Bilder og kategorier:</strong> strukturer produktene tydelig.</li>
              <li className="text__seo-li"><strong>Testing:</strong> sjekk gjestevisningen før lansering.</li>
            </ul>
            <p>
              Hvis du først vil sammenligne hovedtypene av digitale menyer, les siden vår{" "}
              <Link to="/no/digital-meny-for-restauranter">digital meny for restauranter</Link>.
            </p>
          </div>
        </section>

        <section id="bestplace" className="acquaintance">
          <h2>Hvordan fungerer en QR-meny med Best Place?</h2>
          <div className="qrArticle__cardGrid">
            <div>
              <strong>Opprett eller importer menyen</strong>
              <p>Administrer kategorier, navn, priser, beskrivelser og bilder.</p>
            </div>
            <div>
              <strong>Publiser på nett</strong>
              <p>Menyen får sin egen offentlig tilgjengelige side.</p>
            </div>
            <div>
              <strong>Bruk QR-koden</strong>
              <p>Gjestene åpner den samme oppdaterte menyen ved bordet.</p>
            </div>
            <div>
              <strong>Oppdater senere</strong>
              <p>Endringer vises på nett uten at QR-koden må trykkes på nytt.</p>
            </div>
          </div>
        </section>

        <section id="placement" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvor bør QR-koden plasseres i en restaurant?</h2>
            <p>
              En QR-kode fungerer godt bare når gjestene ser den på riktig tidspunkt og enkelt kan skanne den. Vanlige plasseringer er bordskilt, små kort på bordet, disken, vinduer, flyere eller takeaway-emballasje.
            </p>
            <p>
              For trykkstørrelse, materiale, kontrast og testing før produksjon, se guiden vår:{" "}
              <Link to="/no/qr-kode-meny-utskrift">trykking av QR-kode til restaurantmeny</Link>.
            </p>
            <p>
              Hvis fokuset er selve bordskiltet, se også guiden vår:{" "}
              <Link to="/no/restaurant-qr-kode-bordskilt">QR-kode-bordskilt for restauranter</Link>.
            </p>
          </div>
        </section>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om QR-kode-menyer</h2>
          <div className="seoFaq">
            <details>
              <summary>Trenger gjestene en app?</summary>
              <p>
                Nei. En QR-meny kan åpnes direkte i nettleseren på mobilen. Les mer på siden{" "}<Link to="/no/qr-kode-meny-uten-app">QR-kode-meny uten app</Link> page.
              </p>
            </details>
            <details>
              <summary>Må QR-koden trykkes på nytt etter en prisendring?</summary>
              <p>
                Ikke hvis koden peker til en stabil URL for den digitale menyen. Da oppdaterer du bare innholdet i menyen.
              </p>
            </details>
            <details>
              <summary>Kan jeg vise mat og drikke med samme QR-kode?</summary>
              <p>
                Ja. For de fleste restauranter er én meny med tydelige kategorier enklere enn flere forskjellige QR-koder på samme bord.
              </p>
            </details>
            <details>
              <summary>Kan menyen ha flere språk?</summary>
              <p>
                Ja. En digital meny kan tilby flere språkversjoner. Gjesten åpner det samme inngangspunktet og velger deretter språk.
              </p>
            </details>
            <details>
              <summary>Kan jeg teste QR-menyen først?</summary>
              <p>
                Ja. Test gjestevisningen og skanneflyten før du trykker det endelige bordmaterialet.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Relaterte temaer</h2>
          <div className="seoCompareSmall">
            <p><Link to="/no/digital-meny-for-restauranter">Digital meny for restauranter</Link></p>
            <p><Link to="/no/qr-kode-meny-uten-app">QR-meny uten app</Link></p>
            <p><Link to="/no/qr-kode-meny-utskrift">Utskrift av QR-kode-meny</Link></p>
            <p><Link to="/no/administrere-meny-online-for-restauranter">Administrer en meny på nett</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}